import {
  Box,
  Button,
  Center,
  Container,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import type { ActionFunctionArgs } from '@remix-run/node'
import {
  Form,
  redirect,
  useActionData,
  useNavigation,
  useSubmit,
} from '@remix-run/react'
import type { MetaFunction } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { createPasswordResetInquiryCommand } from '~/bootstrap'
import { ERROR_MESSAGES, VALIDATION_MESSAGES } from '~/core/shared/messages'
import { isErrorWithCode } from '~/core/shared/utils/error'
import { generateMetaDescriptor } from '~/core/shared/utils/misc'

export const meta: MetaFunction = (meta) =>
  generateMetaDescriptor(meta, { pageTitle: 'パスワードを忘れた方' })

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const data = Object.fromEntries(formData)

  const emailSchema = z.object({
    email: z
      .string({ message: VALIDATION_MESSAGES.EMPTY_EMAIL })
      .email({ message: VALIDATION_MESSAGES.INVALID_EMAIL })
      .max(1024, { message: VALIDATION_MESSAGES.MAX_LENGTH_EMAIL }),
  })
  const validationResult = emailSchema.safeParse(data)

  if (!validationResult.success)
    return {
      validationErrors: validationResult.error.flatten(),
    }

  const { email } = validationResult.data
  try {
    await createPasswordResetInquiryCommand.execute({ email })
    return redirect('/password/forgot/sendmail')
  } catch (error) {
    const errorCode = isErrorWithCode(error) ? error.errorCode : 5000
    return { applicationError: ERROR_MESSAGES[errorCode] }
  }
}

export const handle = {
  breadcrumb: () => {
    return [
      {
        id: 'password-forgot',
        title: 'パスワードをお忘れの方',
      },
    ]
  },
}

export default function Index() {
  const actionData = useActionData<typeof action>()

  const navigation = useNavigation()
  const submit = useSubmit()
  const [isLoading, setIsLoading] = useState(false)

  const handlePasswordResetFormSubmit = (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault()
    setIsLoading(true)

    const form = event.currentTarget
    submit(form, {
      method: 'post',
      action: '/password/forgot/email',
    })
    setIsLoading(false)
  }

  return (
    <Container my='xl' py='xl' size='sm'>
      <Form
        method='POST'
        action='/password/forgot/email'
        onSubmit={handlePasswordResetFormSubmit}
      >
        <Stack gap='md' w='100%' h='100%'>
          <Title order={3} textWrap='wrap'>
            パスワードを忘れた方
          </Title>
          <Text fw='bold'>
            パスワードを再設定します。メールアドレスを入力してください。
          </Text>
          <TextInput
            name='email'
            size='md'
            label={<Text fw='bold'>メールアドレス</Text>}
            placeholder='メールアドレスを入力してください'
            inputWrapperOrder={['label', 'input', 'error']}
            error={actionData?.validationErrors?.fieldErrors.email?.at(0)}
          />
          {actionData?.applicationError && (
            <Center>
              <Text c='error'>{actionData?.applicationError}</Text>
            </Center>
          )}
          <Box>
            <Center>
              <Button
                type='submit'
                size='xl'
                loading={navigation.state === 'submitting' || isLoading}
              >
                パスワードを変更する
              </Button>
            </Center>
          </Box>
        </Stack>
      </Form>
    </Container>
  )
}
